import { type FC, useCallback, useMemo, useState } from 'react';
import { Card } from '../../../Card';
import {
  type TeamMembersQueryParams,
  type TeamMembersQueryResponse,
  useTeamMembersQuery,
} from '../../../../apiHooks/useTeamMembersQuery';
import styled from 'styled-components';
import { Pagination } from '../../../Pagination';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import ExportActivityLogButton from '../../../Button/DownloadButton/ExportActivityLogButton/ExportActivityLogButton';
import { useTeamMembersFilters } from '../../hooks/useTeamMembersFilters';
import { ActiveFilterPill } from '../ActiveFilterPill';
import type { TeamMembersUser } from '../../../../lib/types/TeamMembersUser';
import { AvatarInitials } from '../../../AvatarInitials/AvatarInitials';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { UserOptionsMenu } from '../UserOptionsMenu';
import { Ellipsis } from '../../../utility/Ellipsis';

const ITEMS_PER_PAGE: number = 10 as const;

export const TeamMembers: FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [teamMembersParams, setTeamMembersParams] = useState<TeamMembersQueryParams>({
    page: { skip: 0, take: ITEMS_PER_PAGE },
    sort: { sortDirection: 1, property: 'cyberiq' },
    filters: [],
  });

  const { data: teamMembersQueryResponse } = useTeamMembersQuery(teamMembersParams);
  const teamMembersData: TeamMembersQueryResponse = teamMembersQueryResponse ?? {
    total: 0,
    skip: null,
    take: null,
    values: [],
  };

  const onNewParams = useCallback((params: Partial<TeamMembersQueryParams>) => {
    if (params.filters) {
      // Reset page filters
      params.page = { skip: 0, take: ITEMS_PER_PAGE };
      setCurrentPage(1);
    }

    setTeamMembersParams((prev) => {
      let mergedFilters = [...(prev.filters || [])];

      if (params.filters) {
        if (params.filters.length === 0) {
          mergedFilters = [];
        } else {
          params.filters.forEach((newFilter) => {
            mergedFilters = mergedFilters.filter((filter) => filter.property !== newFilter.property);
            if (newFilter.value !== '') {
              mergedFilters.push(newFilter);
            }
          });
        }
      }

      return {
        ...prev,
        ...params,
        filters: mergedFilters,
      };
    });
  }, []);

  const { clearFilters } = useTeamMembersFilters(onNewParams);

  const handlePageChange = (page: number) => {
    onNewParams({ page: { take: ITEMS_PER_PAGE, skip: (page - 1) * ITEMS_PER_PAGE } });
    setCurrentPage(page);
  };

  const members = useMemo(() => teamMembersData.values, [teamMembersData.values]);

  return (
    <Card withShadow withBorder>
      <TeamMembersSection>
        <SectionHeader>
          <HeaderMain>
            <Title>
              <NamedContentEntryText refKey="managerSectionTitles" container="span" subKey="teamMembers" />
            </Title>
            {!!teamMembersParams.filters?.length && (
              <ActiveFilterPill onClear={clearFilters} filterCount={teamMembersData.total} />
            )}
          </HeaderMain>
          <ExportActivityLogButton />
        </SectionHeader>
        <TableViewport>
          <MembersTable>
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '4%' }} />
            </colgroup>
            <thead>
              <HeaderRow>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="name" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="jobTitle" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="department" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="cyberIq" />
                </HeaderCell>
                <HeaderCell>
                  <NamedContentEntryText refKey="managerTeamMembers" container="span" subKey="training" />
                </HeaderCell>
                <HeaderActionCell />
              </HeaderRow>
            </thead>
            <tbody>
              {members.map((user) => {
                const progress = getTrainingProgress(user);
                return (
                  <BodyRow key={user.learnerId}>
                    <BodyCell>
                      <MemberIdentity>
                        {user.avatar ? (
                          <AvatarImage src={`data:image/png;base64,${user.avatar}`} alt={user.name} />
                        ) : (
                          <AvatarInitials name={user.name || ''} id={user.learnerId} size="32px" />
                        )}
                        <MemberName>
                          <Ellipsis>{user.name}</Ellipsis>
                        </MemberName>
                      </MemberIdentity>
                    </BodyCell>
                    <BodyCell>
                      <MutedText>
                        <Ellipsis>{user.jobTitle}</Ellipsis>
                      </MutedText>
                    </BodyCell>
                    <BodyCell>
                      <MutedText>
                        <Ellipsis>{user.departmentId}</Ellipsis>
                      </MutedText>
                    </BodyCell>
                    <BodyCell>
                      <PointsCell>
                        <TrendMarker $trend="flat" />
                        <Icon width={16} height={16} icon={IconType.COIN} />
                        <PointsValue>{formatPoints(user.cyberIqPoints)}</PointsValue>
                      </PointsCell>
                    </BodyCell>
                    <BodyCell>
                      <TrainingCell>
                        <TrainingTrack>
                          <TrainingFill $progress={progress} $color={getTrainingColor(progress)} />
                        </TrainingTrack>
                      </TrainingCell>
                    </BodyCell>
                    <ActionCell>
                      <UserOptionsMenu />
                    </ActionCell>
                  </BodyRow>
                );
              })}
            </tbody>
          </MembersTable>
        </TableViewport>
        <Pagination
          pages={Math.ceil(teamMembersData.total / ITEMS_PER_PAGE)}
          currentPage={currentPage}
          perPage={ITEMS_PER_PAGE}
          listSize={teamMembersData.total}
          onChange={handlePageChange}
        />
      </TeamMembersSection>
    </Card>
  );
};

const getTrainingProgress = (user: TeamMembersUser) => {
  if (!user.trainings || user.trainings.assigned === 0) {
    return 0;
  }

  return (user.trainings.completed / user.trainings.assigned) * 100;
};

const getTrainingColor = (progress: number) => {
  if (progress > 60) {
    return 'primary';
  }

  if (progress > 40) {
    return 'managerPrimary';
  }

  return 'error';
};

const formatPoints = (value: number) => value.toLocaleString('en-US');

const TeamMembersSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const TableViewport = styled.div`
  overflow: visible;

  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: visible;
    margin: 0 -4px;
  }
`;

const MembersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;

  @media (max-width: 768px) {
    min-width: 760px;
  }
`;

const HeaderRow = styled.tr`
  border-bottom: 1px solid hsl(220 16% 90% / 0.5);
`;

const HeaderCell = styled.th`
  padding: 0 0 14px;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.normal};
`;

const HeaderActionCell = styled(HeaderCell)`
  width: 32px;
`;

const BodyRow = styled.tr`
  border-bottom: 1px solid hsl(220 16% 90% / 0.5);
  transition: background-color 200ms ease;

  &:hover {
    background-color: hsl(220 14% 96% / 0.3);
  }
`;

const BodyCell = styled.td`
  padding: 14px 0;
  vertical-align: middle;
`;

const MemberIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const AvatarImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex: none;
`;

const MemberName = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ theme }) => theme.fontSizes.normal};
`;

const MutedText = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.normal};
  font-size: ${({ theme }) => theme.fontSizes.normal};
`;

const PointsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.heading};

  > svg {
    flex: none;
  }
`;

const TrendMarker = styled.span<{ $trend: 'up' | 'down' | 'flat' }>`
  display: inline-flex;
  width: 12px;
  height: 12px;
  align-items: center;
  justify-content: center;
  color: ${({ $trend, theme }) =>
    $trend === 'up' ? theme.colors.success : $trend === 'down' ? theme.colors.error : theme.colors.normal};
  flex: none;

  &::before {
    content: ${({ $trend }) => ($trend === 'up' ? "'↗'" : $trend === 'down' ? "'↘'" : "'−'")};
    font-size: ${({ theme }) => theme.fontSizes.normal};
    line-height: 1;
  }
`;

const PointsValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.heading};
`;

const TrainingCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TrainingTrack = styled.div`
  width: 92px;
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.pageBackground};
  overflow: hidden;
`;

const TrainingFill = styled.div<{ $progress: number; $color: 'primary' | 'managerPrimary' | 'error' }>`
  height: 100%;
  width: ${({ $progress }) => `${Math.max(0, Math.min(100, $progress))}%`};
  border-radius: 999px;
  background: ${({ $color, theme }) => theme.colors[$color]};
`;

const ActionCell = styled(BodyCell)`
  width: 32px;
  text-align: right;
`;
